import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, Image as ImageIcon, Key, RefreshCw, Upload, X } from 'lucide-react';
import { getBackendUrl } from '../../../utils/api';

const API = `${getBackendUrl()}/api`;

const WhatsAppAITab = ({ aiConfig, setAiConfig, savedAiConfig, savingAi, showAiKey, setShowAiKey, reloadingAi, uploadingMenuImage, setUploadingMenuImage, handleSaveAiConfig, handleReloadAi }) => {
  const aiProviders = [
    { 
      value: 'pedeefacil', 
      label: '🚀 Pede é Fácil AI (Recomendado)', 
      models: ['gemini-3-flash-preview'],
      isGlobal: true
    },
    { 
      value: 'emergent_custom', 
      label: '🔑 Emergent (Chave Própria)', 
      models: ['gemini-3-flash-preview', 'gpt-5.4-mini', 'gpt-5-mini', 'gpt-5-nano', 'gpt-4o-mini', 'gpt-4o', 'gpt-4-turbo'],
      isEmergentCustom: true
    },
    { 
      value: 'gemini', 
      label: '🤖 Gemini (Google)', 
      models: ['gemini-2.0-flash-exp', 'gemini-1.5-pro', 'gemini-1.5-flash']
    },
    { 
      value: 'openai', 
      label: '⚡ OpenAI (Direta)', 
      models: ['gpt-5.4-mini', 'gpt-5-mini', 'gpt-5-nano', 'gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo', 'gpt-3.5-turbo']
    },
    { 
      value: 'deepseek', 
      label: '🧠 DeepSeek', 
      models: ['deepseek-chat', 'deepseek-coder']
    }
  ];

  return (
            <div className="space-y-6">
              {/* AI Status */}
              {savedAiConfig?.configured && (
                <div className="glass-card border-2 border-purple-500/30 bg-gradient-to-br from-purple-500/10 to-pink-500/10">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-white">IA Configurada</h3>
                      <p className="text-purple-300 text-sm">
                        Provider: <span className="font-semibold">{savedAiConfig.ai_provider}</span>
                        {savedAiConfig.ai_model && ` • Modelo: ${savedAiConfig.ai_model}`}
                      </p>
                    </div>
                    {savedAiConfig.enabled && (
                      <div className="flex items-center gap-2 bg-green-500/20 px-3 py-1 rounded-full">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                        <span className="text-green-400 text-sm font-medium">Ativo</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* AI Configuration Form */}
              <div className="glass-card space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-1">Configurar IA para Pedidos</h3>
                  <p className="text-gray-400 text-sm">Configure qual IA irá processar os pedidos via WhatsApp</p>
                </div>

                {/* ==================== MODO DE OPERAÇÃO ==================== */}
                <div className="rounded-xl border border-white/10 bg-gradient-to-br from-slate-900/60 to-slate-900/30 p-5 space-y-4" data-testid="ai-mode-section">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h4 className="text-base font-semibold text-white flex items-center gap-2">
                        <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                        Modo de Operação
                      </h4>
                      <p className="text-xs text-gray-400 mt-1">
                        Escolha como a IA deve se comportar no WhatsApp
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {/* Active */}
                    <label
                      data-testid="ai-mode-active"
                      className={`cursor-pointer rounded-lg border-2 p-3 transition-all ${
                        (aiConfig.mode || 'active') === 'active'
                          ? 'border-emerald-500 bg-emerald-500/10'
                          : 'border-white/10 bg-black/20 hover:border-white/30'
                      }`}
                    >
                      <input
                        type="radio"
                        name="ai_mode"
                        value="active"
                        checked={(aiConfig.mode || 'active') === 'active'}
                        onChange={() => setAiConfig({ ...aiConfig, mode: 'active' })}
                        className="sr-only"
                      />
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">🤖</span>
                        <span className="text-sm font-semibold text-white">IA Ativa</span>
                      </div>
                      <p className="text-[11px] text-gray-400 leading-snug">
                        A IA responde automaticamente seus clientes e cria pedidos.
                      </p>
                    </label>

                    {/* Passive */}
                    <label
                      data-testid="ai-mode-passive"
                      className={`cursor-pointer rounded-lg border-2 p-3 transition-all ${
                        aiConfig.mode === 'passive'
                          ? 'border-indigo-500 bg-indigo-500/10'
                          : 'border-white/10 bg-black/20 hover:border-white/30'
                      }`}
                    >
                      <input
                        type="radio"
                        name="ai_mode"
                        value="passive"
                        checked={aiConfig.mode === 'passive'}
                        onChange={() => setAiConfig({ ...aiConfig, mode: 'passive' })}
                        className="sr-only"
                      />
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">👂</span>
                        <span className="text-sm font-semibold text-white">IA Externa / Escuta Passiva</span>
                      </div>
                      <p className="text-[11px] text-gray-400 leading-snug">
                        Só escuta a conversa. Você (ou a Meta AI) responde. Cria o pedido quando detectar uma palavra-gatilho.
                      </p>
                    </label>

                    {/* Off */}
                    <label
                      data-testid="ai-mode-off"
                      className={`cursor-pointer rounded-lg border-2 p-3 transition-all ${
                        aiConfig.mode === 'off'
                          ? 'border-rose-500 bg-rose-500/10'
                          : 'border-white/10 bg-black/20 hover:border-white/30'
                      }`}
                    >
                      <input
                        type="radio"
                        name="ai_mode"
                        value="off"
                        checked={aiConfig.mode === 'off'}
                        onChange={() => setAiConfig({ ...aiConfig, mode: 'off' })}
                        className="sr-only"
                      />
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">⏸️</span>
                        <span className="text-sm font-semibold text-white">Desligada</span>
                      </div>
                      <p className="text-[11px] text-gray-400 leading-snug">
                        A IA não responde e não escuta. WhatsApp 100% manual.
                      </p>
                    </label>
                  </div>

                  {/* Configuração específica do Modo Passivo */}
                  {aiConfig.mode === 'passive' && (
                    <div data-testid="passive-config-block" className="mt-2 rounded-lg border border-indigo-500/30 bg-indigo-950/30 p-4 space-y-3">
                      <div className="flex items-start gap-2 text-xs text-indigo-200 bg-indigo-900/40 rounded p-2">
                        <span className="text-base leading-none">🛡️</span>
                        <div>
                          <strong>Proteção contra banimento da Meta:</strong> neste modo a IA <u>não envia</u> nenhuma mensagem. Você (ou a Meta AI nativa) conversa normalmente com o cliente. Quando você digitar uma das frases-gatilho abaixo, o sistema extrai os dados do pedido da conversa e cria automaticamente no painel.
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-200 mb-1">
                          Palavras/frases gatilho <span className="text-gray-500 text-xs">(uma por linha)</span>
                        </label>
                        <p className="text-[11px] text-gray-400 mb-2">
                          Quando <strong>você</strong> enviar qualquer uma destas frases ao cliente, o pedido será criado no painel.
                        </p>
                        <textarea
                          data-testid="passive-triggers-input"
                          value={(aiConfig.passive_trigger_words || []).join('\n')}
                          onChange={(e) => setAiConfig({
                            ...aiConfig,
                            passive_trigger_words: e.target.value.split('\n')
                          })}
                          placeholder={'pedido confirmado\nanotado aqui\nobrigado pela preferência\nseu pedido foi anotado'}
                          className="glass-input w-full font-mono text-sm min-h-[120px]"
                          rows={6}
                        />
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-[11px] text-gray-500">
                            Máx. 20 gatilhos • Detecção ignora maiúsculas/acentos
                          </p>
                          <span className="text-[11px] text-indigo-300">
                            {(aiConfig.passive_trigger_words || []).filter(t => (t || '').trim()).length} ativo(s)
                          </span>
                        </div>
                      </div>

                      <div className="text-[11px] text-amber-200 bg-amber-900/20 border border-amber-500/20 rounded p-2">
                        💡 <strong>Dica:</strong> use frases que você normalmente digita ao finalizar um pedido. Quanto mais naturais e específicas, menor o risco de criar pedidos acidentalmente.
                      </div>
                    </div>
                  )}

                  {aiConfig.mode === 'off' && (
                    <div className="text-xs text-rose-200 bg-rose-950/30 border border-rose-500/20 rounded p-3">
                      ⚠️ Com a IA desligada, nenhuma mensagem é processada automaticamente. Pedidos terão que ser criados manualmente no painel.
                    </div>
                  )}
                </div>
                {/* ==================== /MODO DE OPERAÇÃO ==================== */}

                {/* Provider Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Provider de IA
                  </label>
                  <select
                    value={aiConfig.ai_provider}
                    onChange={(e) => {
                      const provider = aiProviders.find(p => p.value === e.target.value);
                      setAiConfig({
                        ...aiConfig,
                        ai_provider: e.target.value,
                        ai_model: provider?.models[0] || ''
                      });
                    }}
                    className="glass-input w-full"
                  >
                    {aiProviders.map(provider => (
                      <option key={provider.value} value={provider.value}>
                        {provider.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Model Selection - mostrar apenas para providers que precisam */}
                {aiConfig.ai_provider !== 'pedeefacil' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Modelo
                    </label>
                    <select
                      value={aiConfig.ai_model}
                      onChange={(e) => setAiConfig({...aiConfig, ai_model: e.target.value})}
                      className="glass-input w-full"
                    >
                      {aiProviders.find(p => p.value === aiConfig.ai_provider)?.models.map(model => (
                        <option key={model} value={model}>
                          {model}
                        </option>
                      ))}
                    </select>
                    {aiConfig.ai_provider === 'emergent_custom' && (
                      <p className="text-xs text-purple-400 mt-1">
                        💡 Gemini 3 Flash é o padrão recomendado (melhor em cálculos de pedidos)
                      </p>
                    )}
                  </div>
                )}

                {/* API Key - Condicional baseado no provider */}
                {aiConfig.ai_provider === 'pedeefacil' ? (
                  /* Se for Pede é Fácil AI, mostra banner de sucesso sem campo de API */
                  <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <label className="text-sm font-medium text-green-300">
                        ✅ Pede é Fácil AI Ativado
                      </label>
                    </div>
                    
                    <div className="text-xs text-green-200 bg-green-900/30 rounded p-3">
                      <strong>🚀 Você está usando Pede é Fácil AI!</strong><br/><br/>
                      • IA pronta para uso com Gemini 3 Flash<br/>
                      • Tecnologia de ponta para atendimento ao cliente<br/>
                      • Processamento rápido e inteligente de pedidos<br/><br/>
                      <span className="text-green-300">Nenhuma API Key necessária - tudo já está configurado!</span>
                    </div>
                  </div>
                ) : aiConfig.ai_provider === 'emergent_custom' ? (
                  /* Se for Emergent com chave própria */
                  <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Key className="w-5 h-5 text-purple-400" />
                      <label className="text-sm font-medium text-purple-300">
                        🔑 Chave Universal Emergent {savedAiConfig?.configured && savedAiConfig.ai_provider === 'emergent_custom' && '(Configurada)'}
                      </label>
                    </div>
                    
                    <div className="mb-3 text-xs text-purple-200 bg-purple-900/30 rounded p-2">
                      <strong>Use sua própria Chave Universal Emergent</strong><br/>
                      Esta opção permite usar sua conta Emergent pessoal com Gemini 3 Flash (padrão).<br/>
                      <span className="text-purple-300">💡 Obtenha sua chave em: Perfil → Chave Universal</span>
                    </div>
                    
                    <div className="relative">
                      {savedAiConfig?.configured && savedAiConfig.ai_provider === 'emergent_custom' && !showAiKey ? (
                        <div className="flex gap-2">
                          <div className="flex-1 bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-2">
                            <span className="text-gray-400 text-sm font-mono">
                              {savedAiConfig.ai_api_key_masked || '••••••••••••'}
                            </span>
                          </div>
                          <button
                            onClick={() => setShowAiKey(true)}
                            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-all text-sm"
                          >
                            Alterar Key
                          </button>
                        </div>
                      ) : (
                        <>
                          <input
                            type="password"
                            value={aiConfig.ai_api_key}
                            onChange={(e) => setAiConfig({...aiConfig, ai_api_key: e.target.value})}
                            placeholder="Cole sua Chave Universal Emergent (sk-emergent-...)"
                            className="glass-input w-full"
                            required
                          />
                          {showAiKey && (
                            <button
                              onClick={() => {
                                setShowAiKey(false);
                                setAiConfig(prev => ({ ...prev, ai_api_key: '', ai_provider: 'pedeefacil', ai_model: 'gemini-3-flash-preview' }));
                              }}
                              className="mt-2 text-xs text-green-400 hover:text-green-300"
                            >
                              ← Voltar para Pede é Fácil AI (sem API Key)
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Se for outro provider, mostra campo para API Key */
                  <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Key className="w-5 h-5 text-yellow-400" />
                      <label className="text-sm font-medium text-yellow-300">
                        API Key {savedAiConfig?.configured && savedAiConfig.ai_provider !== 'pedeefacil' && '(Configurada)'} - OBRIGATÓRIA
                      </label>
                    </div>
                    
                    <div className="mb-3 text-xs text-yellow-200 bg-yellow-900/30 rounded p-2">
                      ⚠️ <strong>Você selecionou um provider externo</strong><br/>
                      É necessário fornecer sua própria API Key para usar {aiProviders.find(p => p.value === aiConfig.ai_provider)?.label}.<br/>
                      <span className="text-green-300">💡 Dica: Selecione "Pede é Fácil AI" para usar sem API Key!</span>
                    </div>
                    
                    <div className="relative">
                      {savedAiConfig?.configured && savedAiConfig.ai_provider === aiConfig.ai_provider && !showAiKey ? (
                        <div className="flex gap-2">
                          <div className="flex-1 bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-2">
                            <span className="text-gray-400 text-sm font-mono">
                              {savedAiConfig.ai_api_key_masked || '••••••••••••'}
                            </span>
                          </div>
                          <button
                            onClick={() => setShowAiKey(true)}
                            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-all text-sm"
                          >
                            Alterar Key
                          </button>
                        </div>
                      ) : (
                        <>
                          <input
                            type="password"
                            value={aiConfig.ai_api_key}
                            onChange={(e) => setAiConfig({...aiConfig, ai_api_key: e.target.value})}
                            placeholder="Cole sua API Key aqui"
                            className="glass-input w-full"
                            required
                          />
                          {showAiKey && (
                            <button
                              onClick={() => {
                                setShowAiKey(false);
                                setAiConfig(prev => ({ ...prev, ai_api_key: '', ai_provider: 'pedeefacil', ai_model: 'gemini-3-flash-preview' }));
                              }}
                              className="mt-2 text-xs text-green-400 hover:text-green-300"
                            >
                              ← Voltar para Pede é Fácil AI (sem API Key)
                            </button>
                          )}
                        </>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      💡 Obtenha sua key em: 
                      {aiConfig.ai_provider === 'gemini' && ' ai.google.dev'}
                      {aiConfig.ai_provider === 'openai' && ' platform.openai.com'}
                      {aiConfig.ai_provider === 'deepseek' && ' platform.deepseek.com'}
                    </p>
                  </div>
                )}

                {/* Personality Prompt */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                    🎭 Personalidade da IA
                    <span className="text-xs text-purple-400 bg-purple-900/30 px-2 py-1 rounded">Personalizável</span>
                  </label>
                  <textarea
                    value={aiConfig.personality_prompt}
                    onChange={(e) => setAiConfig({...aiConfig, personality_prompt: e.target.value})}
                    rows={8}
                    className="glass-input w-full resize-none font-mono text-sm"
                    placeholder="Defina como a IA deve se comportar ao atender os clientes..."
                  />
                  <div className="mt-2 space-y-2">
                    <p className="text-xs text-gray-400">
                      💡 <strong>Dica:</strong> A IA terá acesso automático a:
                    </p>
                    <ul className="text-xs text-gray-500 ml-4 space-y-1">
                      <li>✅ Cardápio completo com preços e categorias</li>
                      <li>✅ Horários de funcionamento do restaurante</li>
                      <li>✅ Formas de pagamento aceitas</li>
                      <li>✅ Taxas de entrega por bairro</li>
                      <li>✅ Histórico de pedidos do cliente</li>
                      <li>✅ Link do cardápio público online</li>
                    </ul>
                    <details className="mt-3">
                      <summary className="text-xs text-purple-400 cursor-pointer hover:text-purple-300">
                        📝 Ver exemplos de personalidades
                      </summary>
                      <div className="mt-2 p-3 bg-gray-900/50 rounded-lg text-xs text-gray-400 space-y-2">
                        <div>
                          <strong className="text-green-400">Formal e Profissional:</strong>
                          <p className="mt-1 italic">&quot;Seja educado, formal e extremamente profissional. Use sempre &apos;senhor&apos; e &apos;senhora&apos;. Mantenha tom sóbrio e elegante.&quot;</p>
                        </div>
                        <div>
                          <strong className="text-blue-400">Descontraído e Amigável:</strong>
                          <p className="mt-1 italic">&quot;Seja super descontraído e amigável! Use emojis, gírias modernas e deixe o cliente à vontade. Seja como um amigo ajudando a escolher comida 😋"</p>
                        </div>
                        <div>
                          <strong className="text-yellow-400">Especialista Gourmet:</strong>
                          <p className="mt-1 italic">&quot;Você é um especialista em gastronomia. Faça descrições elaboradas dos pratos, sugira harmonizações e destaque ingredientes premium. Eduque o cliente sobre a culinária.&quot;</p>
                        </div>
                        <div>
                          <strong className="text-red-400">Vendedor Persuasivo:</strong>
                          <p className="mt-1 italic">&quot;Seja persuasivo e focado em vendas! Sempre sugira combos, upsell produtos adicionais, destaque promoções e crie senso de urgência.&quot;</p>
                        </div>
                      </div>
                    </details>
                  </div>
                </div>

                {/* Menu Images Upload - Up to 3 images */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
                    <ImageIcon size={16} className="text-green-400" />
                    Imagens do Cardápio (WhatsApp) - Até 6 imagens
                  </label>
                  <p className="text-xs text-gray-500">
                    Quando o cliente pedir o cardápio, estas imagens serão enviadas automaticamente.
                  </p>
                  
                  {/* Grid de imagens existentes */}
                  <div className="grid grid-cols-3 gap-2">
                    {(aiConfig.menu_images || []).map((imageUrl, index) => (
                      <div key={index} className="relative">
                        <img 
                          src={imageUrl} 
                          alt={`Cardápio ${index + 1}`} 
                          className="w-full h-24 object-cover rounded-lg border border-gray-700"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newImages = [...(aiConfig.menu_images || [])];
                            newImages.splice(index, 1);
                            setAiConfig({...aiConfig, menu_images: newImages});
                          }}
                          className="absolute top-1 right-1 p-1 bg-red-500/80 rounded-full hover:bg-red-500"
                        >
                          <X size={12} />
                        </button>
                        <span className="absolute bottom-1 left-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                          {index + 1}
                        </span>
                      </div>
                    ))}
                    
                    {/* Botão de adicionar (se menos de 6 imagens) */}
                    {(aiConfig.menu_images || []).length < 6 && (
                      <div className="border-2 border-dashed border-gray-600 rounded-lg h-24 flex items-center justify-center">
                        <input
                          type="file"
                          accept="image/*"
                          id="menu-image-upload"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            
                            setUploadingMenuImage(true);
                            try {
                              const formData = new FormData();
                              formData.append('file', file);
                              formData.append('category', 'menu');
                              
                              const response = await axios.post(`${API}/upload`, formData);
                              const currentImages = aiConfig.menu_images || [];
                              setAiConfig({
                                ...aiConfig, 
                                menu_images: [...currentImages, response.data.url]
                              });
                              toast.success(`✅ Imagem ${currentImages.length + 1} carregada!`);
                            } catch (error) {
                              console.error('Error uploading menu image:', error);
                              toast.error('Erro ao fazer upload da imagem');
                            } finally {
                              setUploadingMenuImage(false);
                              e.target.value = '';
                            }
                          }}
                        />
                        <label 
                          htmlFor="menu-image-upload"
                          className="cursor-pointer flex flex-col items-center gap-1 text-gray-400 hover:text-gray-300 p-2"
                        >
                          {uploadingMenuImage ? (
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-400" />
                          ) : (
                            <>
                              <Upload size={20} />
                              <span className="text-xs text-center">
                                {(aiConfig.menu_images || []).length === 0 ? 'Adicionar' : '+'}
                              </span>
                            </>
                          )}
                        </label>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>💡 <strong>Palavras-chave:</strong> "cardápio", "menu", "o que tem"</span>
                    <span>{(aiConfig.menu_images || []).length}/6 imagens</span>
                  </div>
                  
                  {/* Toggle para habilitar/desabilitar envio de imagens */}
                  {(aiConfig.menu_images || []).length > 0 && (
                    <div className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg mt-2">
                      <div>
                        <p className="text-white text-sm font-medium">Enviar imagens automaticamente</p>
                        <p className="text-gray-400 text-xs">Quando desabilitado, as imagens não serão enviadas mesmo que o cliente peça</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setAiConfig({...aiConfig, send_menu_images_enabled: !aiConfig.send_menu_images_enabled})}
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                          aiConfig.send_menu_images_enabled !== false ? 'bg-green-500' : 'bg-gray-600'
                        }`}
                      >
                        <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                          aiConfig.send_menu_images_enabled !== false ? 'translate-x-7' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>
                  )}

                  {/* Toggle INDEPENDENTE: enviar imagens junto com a SAUDAÇÃO */}
                  {(aiConfig.menu_images || []).length > 0 && (
                    <div className="flex items-center justify-between p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg mt-2">
                      <div>
                        <p className="text-white text-sm font-medium">👋 Enviar imagens na saudação</p>
                        <p className="text-gray-400 text-xs">Envia as imagens do cardápio automaticamente junto com a mensagem de boas-vindas (primeiro contato do dia)</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setAiConfig({...aiConfig, send_greeting_images_enabled: !aiConfig.send_greeting_images_enabled})}
                        className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${
                          aiConfig.send_greeting_images_enabled === true ? 'bg-blue-500' : 'bg-gray-600'
                        }`}
                      >
                        <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                          aiConfig.send_greeting_images_enabled === true ? 'translate-x-7' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>
                  )}
                </div>

                {/* Response Delay Configuration */}
                <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <label className="text-sm font-medium text-cyan-300">
                      ⏱️ Tempo de Espera para Resposta
                    </label>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min="0"
                      max="60"
                      step="5"
                      value={aiConfig.response_delay ?? 15}
                      onChange={(e) => setAiConfig({...aiConfig, response_delay: parseInt(e.target.value)})}
                      className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                    />
                    <div className="flex items-center gap-1 bg-cyan-900/50 px-3 py-2 rounded-lg min-w-[80px] justify-center">
                      <span className="text-cyan-300 font-bold text-lg">
                        {aiConfig.response_delay === 0 ? '⚡' : (aiConfig.response_delay ?? 15)}
                      </span>
                      <span className="text-cyan-400 text-sm">
                        {aiConfig.response_delay === 0 ? 'Instantâneo' : 'seg'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="bg-gray-900/50 rounded-lg p-3 text-xs space-y-2">
                    <p className="text-gray-300">
                      <strong className="text-cyan-400">💡 Como funciona:</strong> Quando o cliente envia uma mensagem, a IA aguarda este tempo antes de responder.
                    </p>
                    <p className="text-gray-400">
                      Se o cliente enviar mais mensagens durante esse período, o timer reinicia e todas as mensagens são agrupadas em uma única resposta.
                    </p>
                    <p className="text-gray-500 italic">
                      Isso evita que a IA responda várias vezes quando o cliente envia mensagens em sequência rápida.
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-gray-500 pt-1">
                    <span>⚡ Instantâneo (0s)</span>
                    <span>Recomendado: 15-20s</span>
                    <span>🐢 Lento (60s)</span>
                  </div>
                </div>

                {/* Anti-duplicação: silenciar IA após pedido no cardápio digital */}
                <div className="flex items-start justify-between gap-3 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <div className="flex-1">
                    <p className="text-white font-medium flex items-center gap-2">🛡️ Não interagir após pedido no cardápio digital</p>
                    <p className="text-gray-400 text-sm mt-1">
                      Quando ativo, a IA fica <strong className="text-amber-300">em silêncio</strong> (atendimento ativo e escuta passiva) com clientes que fizeram um pedido pelo cardápio digital nas <strong className="text-amber-300">últimas 4 horas</strong>. Evita pedidos duplicados.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setAiConfig({...aiConfig, suppress_ai_after_digital_order: !aiConfig.suppress_ai_after_digital_order})}
                    className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${
                      aiConfig.suppress_ai_after_digital_order === true ? 'bg-amber-500' : 'bg-gray-600'
                    }`}
                  >
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      aiConfig.suppress_ai_after_digital_order === true ? 'translate-x-7' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg">
                  <div>
                    <p className="text-white font-medium">Habilitar IA</p>
                    <p className="text-gray-400 text-sm">Processar pedidos automaticamente via IA</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={aiConfig.enabled}
                      onChange={(e) => setAiConfig({...aiConfig, enabled: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                  </label>
                </div>

                {/* Save Button */}
                <button
                  onClick={handleSaveAiConfig}
                  disabled={savingAi}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold py-4 rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {savingAi ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={20} />
                      Salvar Configurações de IA
                    </>
                  )}
                </button>

                {/* Reload AI Buttons */}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => handleReloadAi(false)}
                    disabled={reloadingAi}
                    className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold py-3 rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
                  >
                    {reloadingAi ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        Atualizando...
                      </>
                    ) : (
                      <>
                        <RefreshCw size={16} />
                        Atualizar IA
                      </>
                    )}
                  </button>

                  <button
                    onClick={() => handleReloadAi(true)}
                    disabled={reloadingAi}
                    className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold py-3 rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
                  >
                    {reloadingAi ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        Reiniciando...
                      </>
                    ) : (
                      <>
                        <RefreshCw size={16} />
                        Reiniciar + Limpar
                      </>
                    )}
                  </button>
                </div>

                {/* Info about reload buttons */}
                <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-4 text-sm">
                  <p className="text-blue-300 font-semibold mb-2">💡 Quando usar estes botões?</p>
                  <div className="space-y-2 text-blue-200 text-xs">
                    <div className="flex items-start gap-2">
                      <span className="text-blue-400">🔄</span>
                      <div>
                        <strong>Atualizar IA:</strong> Use após salvar novas configurações (personalidade, produtos, horários). A IA aplicará as mudanças nas próximas mensagens.
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-orange-400">🔄</span>
                      <div>
                        <strong>Reiniciar + Limpar:</strong> Use se a IA estiver confusa ou com respostas erradas. Apaga TODO o histórico de conversas e reinicia do zero.
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Info Card */}
              <div className="glass-card">
                <h3 className="text-lg font-semibold text-white mb-3">ℹ️ Como Funciona</h3>
                <div className="space-y-3 text-sm text-gray-300">
                  <div className="bg-gray-800/50 rounded-lg p-3">
                    <p className="font-semibold text-white mb-1">📱 Recebimento de Pedidos</p>
                    <p>A IA processará as mensagens recebidas no WhatsApp e identificará pedidos automaticamente</p>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-3">
                    <p className="font-semibold text-white mb-1">🤖 Processamento Inteligente</p>
                    <p>Extrai itens, quantidades, endereço e forma de pagamento das mensagens dos clientes</p>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-3">
                    <p className="font-semibold text-white mb-1">✅ Confirmação Automática</p>
                    <p>A IA confirma os pedidos com o cliente antes de salvar no sistema</p>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-3">
                    <p className="font-semibold text-white mb-1">🔔 Notificações de Status</p>
                    <p>Envia atualizações automáticas sobre o status do pedido diretamente no WhatsApp</p>
                  </div>
                </div>
              </div>
            </div>
  );
};

export default WhatsAppAITab;
